'use client';

import { useEffect } from 'react';
import { useAppStore } from '@/features/shared/app-store';
import { AdPlacement } from '@/features/ads/shared/types/ad-placement';
import { useMultiTimeout } from '@/shared/hooks/use-multi-timeout';
import { useAdsStore } from '@/features/ads/internal/state/ads-slice';
import { useVisiblePlacements } from '@/features/ads/internal/hooks/use-visible-placements';
import {
  selectConsentFramework,
  selectConsentReady,
} from '@/features/consent/shared/state/selectors';
import { initializePlacements } from '@/features/ads/internal/helper/initialize-placements';

interface AdsInitializedProps {
  placements: AdPlacement[];
}

const AdsPlacementInitializer = ({ placements }: AdsInitializedProps) => {
  const { set: setTimeoutForSlot, clearAll } = useMultiTimeout();
  const gptLoaded = useAdsStore((state) => state.gptLoaded);
  const pbjsLoaded = useAdsStore((state) => state.pbjsLoaded);
  const consentReady = useAppStore(selectConsentReady);
  const consentFramework = useAppStore(selectConsentFramework);

  const visiblePlacements = useVisiblePlacements(placements);

  const readyToInitialize =
    gptLoaded && consentReady && pbjsLoaded !== null && visiblePlacements.length > 0;

  useEffect(() => {
    if (!readyToInitialize) return;
    const { pbjs, googletag } = window;
    if (!googletag) return;

    const shouldUsePrebid = pbjsLoaded && pbjs;

    const cleanup = initializePlacements({
      placements: visiblePlacements,
      googletag,
      pbjs: shouldUsePrebid ? pbjs : undefined,
      consentFramework,
      setTimeoutForSlot,
      clearAll,
    });

    return () => {
      cleanup();
    };
  }, [
    clearAll,
    consentFramework,
    pbjsLoaded,
    readyToInitialize,
    setTimeoutForSlot,
    visiblePlacements,
  ]);

  return null;
};

export const AdsPlacementsInitializer = AdsPlacementInitializer;
